import {Comment, CommentInfo} from '../../repository/NatGetDisasterComment'
import {Moment} from 'moment'
import * as React from 'react'
import {ReactElement} from 'react'
import {Box} from '@mui/material'
import {DisplayItemTitle} from '../../../../../styledComponents/DisplayItem'
import NatResidentDisasterCommentItem from './NatResidentDisasterCommentItem'
import {NatPostDisasterCommentRequest} from '../../repository/NatPostDisasterComment'
import {Nullable} from '../../../../../app/TypeHelpers'
import {Messages} from '../../../../../Messages'
import styled from 'styled-components'
import {BreakpointValues, Colors, FontSize} from '../../../../../styledComponents/Constants'
import {MESSAGE_TYPE, VIEW_DO_OR_NOT, YES_OR_NOT} from '../../../../../CodeMaster'
import {NatPostDisasterCommentFileUploadRequest} from '../../repository/NatPostDisasterCommentFileUpload'
import {currentMedia} from '../../../../../styledComponents/ResponsiveConvenience'
import {NatPutDisasterCommentRequest} from '../../repository/NatPutDisasterComment'
import NatResidentMessageDialogComponent from '../../../common/resident/NatResidentMessageDialogComponent'
import {DeleteForever} from '@mui/icons-material'
import NatDisasterCommentEditDialog from './NatDisasterCommentEditDialog'
import {InputError} from './NatDisasterCommentError'
import {DisasterCommentFile} from './NatDisasterCommentInputForm'
import {ImageViewDialog} from './NatDisasterImageViewDialog'
import {CookieHelper} from '../../../../../CookieHelper'
import {KEY_DISASTER_COMMENT_NAME_PRIVATE} from './NatDisasterCommentArea'

interface Props {
    pastFlag?: boolean,
    condominiumCd: string
    topicId: number
    comments: CommentInfo[]
    commentReadMap: Map<number, number>
    searchKeyword: string
    currentDateTime: Moment
    onReply: (
        commentRequest: NatPostDisasterCommentRequest,
        fileRequests: NatPostDisasterCommentFileUploadRequest[]
    ) => Promise<Nullable<InputError>>
    onEdit: (
        commentId: number,
        commentRequest: NatPutDisasterCommentRequest,
        fileRequests: NatPostDisasterCommentFileUploadRequest[]
    ) => Promise<Nullable<InputError>>
    onDelete: (
        commentId: number
    ) => Promise<void>
    onReadComments: (commentIds: number[]) => Promise<void>
    onClearFileUploadError: () => void
    fileUploadError: Nullable<InputError>
}

type DialogContentType = 'delete' | 'edit' | 'preview'

interface State {
    previewStartIndex: number
    targetCommentInfo: Nullable<Comment>
    dialogStatus: Nullable<DialogContentType>
}

export default class NatResidentDisasterCommentList extends React.PureComponent<Props, State> {

    constructor(props: Readonly<Props>) {
        super(props)
        this.state = {
            previewStartIndex: 0,
            targetCommentInfo: null,
            dialogStatus: null
        }
    }

    handleDeleteCommentDialogOpen = (comment: Comment): void => {
        this.setState({
            dialogStatus: 'delete',
            targetCommentInfo: comment
        })
    }

    handleEditCommentDialogOpen = (comment: Comment): void => {
        this.setState({
            dialogStatus: 'edit',
            targetCommentInfo: comment
        })
    }

    handlePreviewDialogDialogOpen = (comment: Comment, previewStartIndex: number): void => {
        this.setState({
            dialogStatus: 'preview',
            targetCommentInfo: comment,
            previewStartIndex: previewStartIndex
        })
    }

    handleDialogCansel = (): void => {
        this.setState({
            dialogStatus: null,
            targetCommentInfo: null,
            previewStartIndex: 0
        })
    }

    handleReplyComment = async (
        commentRequest: NatPostDisasterCommentRequest,
        fileRequests: NatPostDisasterCommentFileUploadRequest[]
    ): Promise<Nullable<InputError>> => {
        return await this.props.onReply(commentRequest, fileRequests)
    }

    handleDeleteComment = async (): Promise<void> => {
        if (!this.state.targetCommentInfo) {
            return
        }
        const {commentId} = this.state.targetCommentInfo
        await this.props.onDelete(commentId)
        this.handleDialogCansel()
    }

    handleEditComment = async (
        commentRequest: NatPutDisasterCommentRequest,
        fileRequests: NatPostDisasterCommentFileUploadRequest[]
    ): Promise<Nullable<InputError>> => {
        if (!this.state.targetCommentInfo) {
            return null
        }
        const {commentId} = this.state.targetCommentInfo
        return await this.props.onEdit(commentId, commentRequest, fileRequests)
    }

    renderDeleteDialog(): ReactElement {
        if (!this.state.targetCommentInfo) {
            return <></>
        }

        const message = this.state.targetCommentInfo.isPosterSelf === YES_OR_NOT.YES.value ?
            Messages.IOPCC0029.toString('コメント') : Messages.IDISC0002.message

        return (
            <NatResidentMessageDialogComponent
                open={this.state.dialogStatus === 'delete'}
                params={{
                    title: '削除確認',
                    titleIcon: <DeleteTitleIconWrapper><DeleteForever/></DeleteTitleIconWrapper>,
                    errorLevel: MESSAGE_TYPE.ERROR,
                    message: message,
                    buttonProps: [
                        {
                            isDefault: false,
                            buttonLabel: '削除する',
                            buttonHandler: this.handleDeleteComment,
                            color: Colors.Alert
                        },
                        {
                            isDefault: false,
                            buttonLabel: 'キャンセル',
                            buttonHandler: this.handleDialogCansel
                        }
                    ]
                }}
            />
        )
    }

    renderEditDialog(): ReactElement {
        if (!this.state.targetCommentInfo) {
            return <></>
        }

        const {
            commentId,
            comment,
            posterNamePrivateFlag,
            commentToAttachment,
            version
        } = this.state.targetCommentInfo

        const commentFileList: DisasterCommentFile[] = commentToAttachment.map(file => {
            return {
                fileNumber: file.seq,
                fileName: '',
                fileData: file.previewPath,
                fileSize: 0
            }
        })

        return (
            <NatDisasterCommentEditDialog
                open={this.state.dialogStatus === 'edit'}
                commentId={commentId}
                version={version}
                comment={comment ?? ''}
                posterNamePrivateFlag={posterNamePrivateFlag === YES_OR_NOT.YES.value}
                commentFileList={commentFileList}
                deleteFileNumberList={[]}
                onClickEditCanselButton={this.handleDialogCansel}
                onClickEditButton={this.handleEditComment}
            />
        )
    }

    renderPreviewDialog(): ReactElement {
        if (!this.state.targetCommentInfo) {
            return <></>
        }

        const {commentToAttachment} = this.state.targetCommentInfo

        const images = commentToAttachment.map(file => {
            return {
                srcSet: `
                  ${file.previewPath} 900w,
                  ${file.PreviewLargePath} 1600w
                `,
                sizes: `(max-width: ${BreakpointValues.md}px) 100vw`,
                original: file.PreviewLargePath
            }
        })

        return (
            <ImageViewDialog
                open={this.state.dialogStatus === 'preview'}
                src={images}
                onClose={this.handleDialogCansel}
                startIndex={this.state.previewStartIndex}
            />
        )
    }

    render(): ReactElement {
        const {pastFlag, comments, currentDateTime} = this.props

        return (
            <>
                {this.renderDeleteDialog()}
                {this.renderEditDialog()}
                {this.renderPreviewDialog()}
                <CommentArea>
                    <Box padding={'20px 15px'}>
                        <DisplayItemTitle>コメント</DisplayItemTitle>
                    </Box>
                    {comments.length === 0 &&
                        <Box padding={'0 15px 15px'}>
                            {this.props.searchKeyword ?
                                '該当するコメントはありません' : 'コメントはありません'}
                        </Box>
                    }
                    {comments.map((commentInfo) =>
                        <NatResidentDisasterCommentItem
                            key={commentInfo.commentId}
                            pastFlag={pastFlag}
                            commentInfo={commentInfo}
                            commentReadMap={this.props.commentReadMap}
                            searchKeyword={this.props.searchKeyword}
                            posterNamePrivateFlag={CookieHelper.getStringValue(KEY_DISASTER_COMMENT_NAME_PRIVATE)
                                === VIEW_DO_OR_NOT.HIDDEN.value}
                            currentDateTime={currentDateTime}
                            onClickReplyButton={this.handleReplyComment}
                            onClickDeleteButton={this.handleDeleteCommentDialogOpen}
                            onClickEditButton={this.handleEditCommentDialogOpen}
                            onClickPreviewButton={this.handlePreviewDialogDialogOpen}
                            onReadComment={this.props.onReadComments}
                            onClearFileUploadError={this.props.onClearFileUploadError}
                            fileUploadError={this.props.fileUploadError}
                        />
                    )}
                </CommentArea>
            </>
        )
    }
}

const CommentArea = styled.div`
    background-color: ${Colors.Surface};
    width: calc(100% + 30px);
    margin: 0 -15px;
`

const DeleteTitleIconWrapper = styled.div`
    line-height: 1;

    svg {
        color: ${Colors.Alert};
        font-size: ${FontSize.ExtraBig} !important;
        ${currentMedia.lessThan('sp')`
          font-size: ${FontSize.Big}!important;
        `};
    }
`